import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Paper,
  IconButton,
  TextField,
  Menu,
  Tooltip
} from '@mui/material';
import { Mic, MicOff, Save, Delete, Edit } from '@mui/icons-material';
// import pdfjs-dist dynamically when needed
import { saveNote } from '../../utils/supabase';
import { auth } from '../../utils/auth';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { generateContent } from '../../utils/ai';

function StructuredNotes() {
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [notes, setNotes] = useState([]);
  const [spokenText, setSpokenText] = useState(''); // Store spoken text
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [user, setUser] = useState(null);
  const notesEndRef = useRef(null);

  const languages = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    { code: 'hi-IN', name: 'Hindi' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'zh-CN', name: 'Chinese (Simplified)' },
  ];

  useEffect(() => {
    scrollToBottom();
  }, [notes]);

  const scrollToBottom = () => {
    notesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const initUser = async () => {
      const currentUser = await auth.getCurrentUser();
      setUser(currentUser);
    };
    initUser();
    initializeSpeechRecognition();
  }, [selectedLanguage]);

  const initializeSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || 
                             window.webkitSpeechRecognition || 
                             window.mozSpeechRecognition || 
                             window.msSpeechRecognition;

    if (SpeechRecognition) {
      try {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = selectedLanguage;

        recognition.onstart = () => {
          setIsRecording(true);
          setError('');
          setSpokenText(''); // Reset spoken text when starting
        };

        recognition.onend = () => {
          setIsRecording(false);
        };

        recognition.onresult = (event) => {
          let finalTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            }
          }
          setSpokenText(prev => prev + finalTranscript); // Accumulate spoken text
        };

        recognition.onerror = (event) => {
          handleRecognitionError(event.error);
        };

        setRecognition(recognition);
        setError('');
      } catch (err) {
        setError('Failed to initialize speech recognition. Please try a different browser.');
        console.error('Speech Recognition Init Error:', err);
      }
    } else {
      setError('Speech recognition is not supported in your browser. Please try Chrome, Edge, or Safari.');
    }
  };

  const handleRecognitionError = (error) => {
    if (error === 'not-allowed') {
      setError('Microphone access denied. Please allow microphone access in your browser settings.');
    } else if (error === 'no-speech') {
      setError('No speech detected. Please try speaking again.');
    } else {
      setError(`Error recording: ${error}`);
    }
    setIsRecording(false);
  };

  const startRecording = () => {
    if (!recognition) {
      initializeSpeechRecognition();
      return;
    }

    try {
      recognition.start();
      setError('');
    } catch (err) {
      setError('Failed to start recording. Please refresh the page and try again.');
      console.error('Start Recording Error:', err);
    }
  };

  const stopRecording = () => {
    if (recognition) {
      try {
        recognition.stop();
      } catch (err) {
        setError('Failed to stop recording. Please refresh the page and try again.');
        console.error('Stop Recording Error:', err);
      }
    }
  };

  const handleGenerateNotes = async () => {
    if (!spokenText.trim()) return;

    setLoading(true);
    try {
      const messages = [{
        role: 'system',
        content: 'generate exam styled notes for: ' + spokenText.trim()
      }, {
        role: 'user',
        content: spokenText.trim()
      }];

      const response = await generateContent(messages, { max_tokens: 2000, temperature: 0.7 });
      setNotes(prev => [...prev, { content: response, timestamp: new Date().toLocaleTimeString() }]);
      setSpokenText('');
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to generate notes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatNote = (text) => {
    // Simple markdown formatting
    if (text.toLowerCase().startsWith('header ')) {
      return `# ${text.replace(/header /i, '')}`;
    }
    if (text.toLowerCase().startsWith('subheader ')) {
      return `## ${text.replace(/subheader /i, '')}`;
    }
    if (text.toLowerCase().startsWith('list item ')) {
      return `- ${text.replace(/list item /i, '')}`;
    }
    return text;
  };

  const handleSaveClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSaveToDatabase = async () => {
    if (!user) {
      setError('Please login to save notes');
      return;
    }
    try {
      await saveNote(user.id, JSON.stringify(notes));
      setError('');
    } catch (err) {
      setError('Failed to save notes to database');
    }
    handleClose();
  };

  const handleSaveAsPDF = async () => {
    try {
      setLoading(true);
      const pdf = await import('pdfjs-dist');
      pdf.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).href;
      
      // Create PDF content
      const content = notes.map(note => `${note.timestamp}\n${note.content}\n\n`).join('');
      
      // Create a Blob containing the text content
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      
      // Create and click a download link
      const link = document.createElement('a');
      link.href = url;
      link.download = 'notes.pdf';
      link.click();
      
      // Clean up
      URL.revokeObjectURL(url);
      handleClose();
    } catch (error) {
      setError('Failed to generate PDF');
      console.error('PDF Generation Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = (index) => {
    const newNotes = [...notes];
    newNotes.splice(index, 1);
    setNotes(newNotes);
  };

  const handleEditNote = (index) => {
    setEditingNoteIndex(index);
    setSpokenText(''); // Clear spoken text to record new changes
    startRecording();
  };

  const handleEditingNoteUpdate = async () => {
    if (editingNoteIndex === null || !spokenText.trim()) return;

    setLoading(true);
    try {
      const currentNote = notes[editingNoteIndex].content;
      const changes = spokenText.trim();

      const messages = [{
        role: 'system',
        content: `Edit the following note based on these changes: Current note: "${currentNote}". Changes: "${changes}". Return only the updated note content.`
      }, {
        role: 'user',
        content: changes
      }];

      const updatedContent = await generateContent(messages, { max_tokens: 2000, temperature: 0.7 });

      // Update the note with AI-generated content
      const newNotes = [...notes];
      newNotes[editingNoteIndex].content = updatedContent;
      setNotes(newNotes);
      setEditingNoteIndex(null);
      setSpokenText(''); // Clear after updating
      stopRecording();
      setError(''); // Clear any previous errors
    } catch (err) {
      setError(err.message || 'Failed to update note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel id="language-label">Language</InputLabel>
            <Select
              labelId="language-label"
              id="language-select"
              value={selectedLanguage}
              label="Language"
              onChange={(e) => setSelectedLanguage(e.target.value)}
            >
              {languages.map((language) => (
                <MenuItem key={language.code} value={language.code}>
                  {language.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Spoken Text"
            value={spokenText}
            onChange={(e) => setSpokenText(e.target.value)} // Allow manual editing
            sx={{ width: 300 }}
            multiline
            rows={2}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateNotes}
            disabled={!spokenText.trim()}
            sx={{ ml: 2 }}
          >
            Generate Notes
          </Button>
        </Box>

        <Box>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSaveClick}
            startIcon={<Save />}
            sx={{ mr: 2 }}
          >
            Save Notes
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleSaveToDatabase}>Save to Database</MenuItem>
            <MenuItem onClick={handleSaveAsPDF}>Save as PDF</MenuItem>
          </Menu>
          
          <IconButton
            color={isRecording ? 'secondary' : 'default'}
            onClick={isRecording ? stopRecording : startRecording}
            size="large"
          >
            {isRecording ? (
              <MicOff fontSize="large" />
            ) : (
              <Mic fontSize="large" />
            )}
          </IconButton>
          {editingNoteIndex !== null && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleEditingNoteUpdate}
              sx={{ ml: 2 }}
              disabled={!isRecording || loading}
            >
              Update Note
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ 
        flexGrow: 1, 
        m: 2, 
        p: 2, 
        overflow: 'auto',
        bgcolor: 'background.default'
      }}>
        {notes.map((note, index) => (
          <Box key={index} sx={{ mb: 2, position: 'relative' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {note.timestamp}
            </Typography>
            <ReactMarkdown components={{p: ({node, ...props}) => <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }} {...props} />}}>
              {note.content}
            </ReactMarkdown>
            <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
              <Tooltip title="Edit Note">
                <IconButton
                  size="small"
                  onClick={() => handleEditNote(index)}
                  color={editingNoteIndex === index ? 'primary' : 'default'}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Note">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteNote(index)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}
        <div ref={notesEndRef} />
      </Paper>

      {loading && (
        <Box sx={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(255, 255, 255, 0.8)'
        }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ p: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

export default StructuredNotes;